import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/layout/Logo";
import ThemeToggle from "@/components/widgets/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();
  const nextParam = new URLSearchParams(loc.search).get("next");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (!r.ok) return toast.error(r.error);
    toast.success("Welcome back!");
    const dest = nextParam || (r.user.role === "admin" ? "/admin" : "/app");
    nav(dest);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-10 relative overflow-hidden brand-gradient text-white">
        <div className="relative z-10"><Logo /></div>
        <div className="relative z-10 max-w-md space-y-6">
          <h2 className="font-display text-4xl leading-tight">Finance that feels like<br/>a personal concierge.</h2>
          <p className="text-white/80">Sign in to track applications, view your repayment schedule, and access curated loan products built for salaried professionals.</p>
          <div className="grid grid-cols-3 gap-3 pt-4">
            <div className="glass rounded-xl p-4 bg-white/10 border-white/20"><div className="text-2xl font-display">200K+</div><div className="text-xs opacity-80">Customers served</div></div>
            <div className="glass rounded-xl p-4 bg-white/10 border-white/20"><div className="text-2xl font-display">₹850Cr</div><div className="text-xs opacity-80">Disbursed*</div></div>
            <div className="glass rounded-xl p-4 bg-white/10 border-white/20"><div className="text-2xl font-display">4.8★</div><div className="text-xs opacity-80">User rating</div></div>
          </div>
          <div className="text-xs opacity-60">*Illustrative figures for demo purposes.</div>
        </div>
        <div className="hero-orb h-96 w-96 bg-white/30" style={{ top: "-4rem", right: "-4rem" }} />
        <div className="hero-orb h-72 w-72 bg-white/20" style={{ bottom: "-4rem", left: "-4rem" }} />
      </div>

      <div className="flex flex-col p-6 lg:p-10 max-w-md mx-auto w-full">
        <div className="flex items-center justify-between">
          <div className="lg:hidden"><Logo /></div>
          <div className="ml-auto"><ThemeToggle /></div>
        </div>
        <div className="flex-1 flex flex-col justify-center py-10">
          <h1 className="font-display text-3xl mb-2">Welcome back</h1>
          <p className="text-sm text-muted-foreground mb-8">Sign in to your Finzpe account.</p>
          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" required autoComplete="email" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input id="password" data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} type={show ? "text" : "password"} required autoComplete="current-password" />
                <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
              </div>
            </div>
            <div className="text-right"><Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="link-forgot">Forgot password?</Link></div>
            <Button type="submit" disabled={loading} className="w-full brand-gradient text-white rounded-full h-11" data-testid="login-submit">{loading ? "Signing in..." : "Sign in"}</Button>
          </form>
          <div className="mt-6 text-sm text-muted-foreground">Don't have an account? <Link to="/register" className="text-primary font-medium">Create one</Link></div>
          <div className="mt-6 p-4 rounded-xl bg-muted text-xs space-y-1">
            <div className="font-medium">Demo accounts</div>
            <div>Customer: <code>customer@finzpe.com</code> / <code>Customer@123</code></div>
            <div>Admin: <code>admin@finzpe.com</code> / <code>Admin@123</code></div>
          </div>
        </div>
      </div>
    </div>
  );
}
